import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  IconButton, 
  Chip,
  Collapse,
  Avatar,
  Stack,
  Divider,
  CircularProgress
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import PersonIcon from '@mui/icons-material/Person';
import { useAnalytics } from '../hooks/useAnalytics';

// Animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

// Styled Components
const AssistantContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: '24px',
  right: '24px',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: theme.spacing(2)
}));

const ChatWindow = styled(Paper)(({ theme }) => ({
  width: '380px',
  height: '500px',
  borderRadius: '24px',
  background: theme.palette.mode === 'dark' 
    ? 'rgba(26, 32, 46, 0.95)' 
    : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 20px 60px rgba(0, 0, 0, 0.5)'
    : '0 20px 60px rgba(0, 0, 0, 0.15)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    width: 'calc(100vw - 48px)',
    height: '400px'
  }
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  color: '#ffffff',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const ChatBody = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  '&::-webkit-scrollbar': {
    width: '6px'
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.grey[400],
    borderRadius: '3px'
  }
}));

const ChatFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center'
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isBot'
})<{ isBot: boolean }>(({ theme, isBot }) => ({
  maxWidth: '75%',
  padding: theme.spacing(1.5, 2),
  borderRadius: '18px',
  backgroundColor: isBot 
    ? theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
    : theme.palette.primary.main,
  color: isBot ? theme.palette.text.primary : '#ffffff',
  alignSelf: isBot ? 'flex-start' : 'flex-end',
  position: 'relative',
  wordBreak: 'break-word',
  '&::before': isBot ? {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '-8px',
    transform: 'translateY(-50%)',
    width: 0,
    height: 0,
    borderTop: '8px solid transparent',
    borderBottom: '8px solid transparent',
    borderRight: `8px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`
  } : {
    content: '""',
    position: 'absolute',
    top: '50%',
    right: '-8px',
    transform: 'translateY(-50%)',
    width: 0,
    height: 0,
    borderTop: '8px solid transparent',
    borderBottom: '8px solid transparent',
    borderLeft: `8px solid ${theme.palette.primary.main}`
  }
}));

const FloatingButton = styled(IconButton)(({ theme }) => ({
  width: '64px',
  height: '64px',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  color: '#ffffff',
  boxShadow: '0 8px 30px rgba(0, 102, 204, 0.4)',
  animation: `${float} 3s ease-in-out infinite`,
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
    animation: `${pulse} 0.6s ease-in-out`,
    transform: 'scale(1.1)',
    boxShadow: '0 12px 40px rgba(0, 102, 204, 0.6)'
  },
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
}));

const QuickActions = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  gap: theme.spacing(1)
}));

// Types
interface Message {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: Date;
  type?: 'text' | 'action' | 'suggestion';
}

interface AIResponse {
  text: string;
  suggestions?: string[];
  actions?: Array<{
    label: string;
    action: string;
  }>;
}

// AI Assistant Component
const AIAssistant: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const analytics = useAnalytics();

  // Initialize with welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage: Message = {
        id: 'welcome',
        text: 'Merhaba! Ben Mert\'in AI asistanıyım. Size nasıl yardımcı olabilirim? Projeler, beceriler veya iletişim hakkında sorularınızı yanıtlayabilirim.',
        isBot: true,
        timestamp: new Date(),
        type: 'text'
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, messages.length]);

  // Auto scroll to bottom
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages]);

  // Quick action suggestions
  const quickActions = [
    { label: '🚀 Projeler', action: 'Hangi projeler üzerinde çalışıyorsun?' },
    { label: '💻 Teknolojiler', action: 'Hangi teknolojileri kullanıyorsun?' },
    { label: '📧 İletişim', action: 'Nasıl iletişim kurabilirim?' },
    { label: '🎓 Deneyim', action: 'Deneyimin nedir?' }
  ];

  // AI Knowledge base
  const getAIResponse = (userMessage: string): AIResponse => {
    const message = userMessage.toLowerCase();
    
    if (message.includes('proje')) {
      return {
        text: 'Şu anda AI Chatbot Platform, Blockchain DeFi Dashboard ve Machine Learning modelleri üzerinde çalışıyorum. React, TypeScript, Python ve Solidity kullanarak modern web uygulamaları geliştiriyorum.',
        suggestions: ['Hangi teknolojileri kullanıyorsun?', 'GitHub profilini görebilir miyim?']
      };
    }
    
    if (message.includes('teknoloji') || message.includes('skill') || message.includes('beceri')) {
      return {
        text: 'Frontend: React, TypeScript, Next.js, Material-UI\nBackend: Python, FastAPI, Node.js\nAI/ML: TensorFlow, PyTorch, scikit-learn\nBlockchain: Solidity, Web3.js, Ethers.js\nDevOps: Docker, AWS, Vercel',
        suggestions: ['Hangi projeler üzerinde çalışıyorsun?', 'Deneyimin nedir?']
      };
    }
    
    if (message.includes('iletişim') || message.includes('contact') || message.includes('email')) {
      return {
        text: 'Benimle şu kanallardan iletişim kurabilirsiniz:\n📧 Email: mert@example.com\n💼 LinkedIn: linkedin.com/in/mertsahsi\n🐙 GitHub: github.com/mertsahsi',
        actions: [
          { label: 'Email Gönder', action: 'mailto:mert@example.com' },
          { label: 'LinkedIn\'e Git', action: 'https://linkedin.com/in/mertsahsi' }
        ]
      };
    }
    
    if (message.includes('deneyim') || message.includes('experience')) {
      return {
        text: '2+ yıl web geliştirme deneyimi, 15+ farklı teknoloji ile çalışma tecrübesi ve 10+ tamamlanmış proje. Bilgisayar mühendisliği öğrencisi olarak AI ve Web3 alanlarında uzmanlaşıyorum.',
        suggestions: ['Hangi üniversitede okuyorsun?', 'En sevdiğin proje hangisi?']
      };
    }
    
    if (message.includes('merhaba') || message.includes('selam') || message.includes('hi') || message.includes('hello')) {
      return {
        text: 'Merhaba! Nasılsınız? Size nasıl yardımcı olabilirim? Projelerim, becerilerim veya benimle iletişim kurmak hakkında sorularınızı yanıtlayabilirim.',
        suggestions: ['Projelerini göster', 'Hangi teknolojileri kullanıyorsun?']
      };
    }
    
    // Default response
    return {
      text: 'Üzgünüm, bu konuda tam olarak yanıt veremiyorum. Ancak projeler, teknolojiler, deneyim ve iletişim hakkında sorularınızı yanıtlayabilirim. Lütfen daha spesifik bir soru sorun!',
      suggestions: ['Projelerini göster', 'Teknolojilerini listele', 'İletişim bilgileri']
    };
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      isBot: false,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Track AI interaction
    analytics.trackCustomEvent('ai_assistant_interaction', {
      message: inputValue,
      timestamp: Date.now(),
      event_category: 'ai_assistant'
    });

    // Simulate typing delay
    setTimeout(() => {
      const aiResponse = getAIResponse(inputValue);
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponse.text,
        isBot: true,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);

      // Add suggestions if available
      if (aiResponse.suggestions) {
        setTimeout(() => {
          aiResponse.suggestions!.forEach((suggestion, index) => {
            setTimeout(() => {
              const suggestionMessage: Message = {
                id: `suggestion-${Date.now()}-${index}`,
                text: suggestion,
                isBot: true,
                timestamp: new Date(),
                type: 'suggestion'
              };
              setMessages(prev => [...prev, suggestionMessage]);
            }, index * 500);
          });
        }, 1000);
      }
    }, 1500);
  };

  const handleQuickAction = (action: string) => {
    setInputValue(action);
    handleSendMessage();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      analytics.trackCustomEvent('ai_assistant_opened', {
        timestamp: Date.now(),
        event_category: 'ai_assistant'
      });
    }
  };

  return (
    <AssistantContainer>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <ChatWindow>
              <ChatHeader>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
                    <SmartToyIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      AI Assistant
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Online • Hemen yanıtlıyor
                    </Typography>
                  </Box>
                </Stack>
                <IconButton 
                  onClick={toggleChat}
                  sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                >
                  <CloseIcon />
                </IconButton>
              </ChatHeader>

              <ChatBody ref={chatBodyRef}>
                {messages.length === 1 && (
                  <QuickActions direction="column">
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Hızlı sorular:
                    </Typography>
                    {quickActions.map((action, index) => (
                      <motion.div
                        key={action.label}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Chip
                          label={action.label}
                          onClick={() => handleQuickAction(action.action)}
                          variant="outlined"
                          color="primary"
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { 
                              backgroundColor: 'primary.main',
                              color: 'primary.contrastText'
                            }
                          }}
                        />
                      </motion.div>
                    ))}
                  </QuickActions>
                )}

                {messages.map((message, index) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Stack 
                      direction="row" 
                      spacing={1} 
                      alignItems="flex-start"
                      sx={{ mb: 2 }}
                    >
                      {message.isBot && (
                        <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                          <SmartToyIcon sx={{ fontSize: 14 }} />
                        </Avatar>
                      )}
                      <MessageBubble isBot={message.isBot}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {message.text}
                        </Typography>
                        {message.type === 'suggestion' && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleQuickAction(message.text)}
                            sx={{ mt: 1, p: 0, minWidth: 0 }}
                          >
                            <QuestionAnswerIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            Sor
                          </Button>
                        )}
                      </MessageBubble>
                      {!message.isBot && (
                        <Avatar sx={{ width: 24, height: 24, bgcolor: 'secondary.main' }}>
                          <PersonIcon sx={{ fontSize: 14 }} />
                        </Avatar>
                      )}
                    </Stack>
                  </motion.div>
                ))}

                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                        <SmartToyIcon sx={{ fontSize: 14 }} />
                      </Avatar>
                      <MessageBubble isBot={true}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CircularProgress size={16} />
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            Yazıyor...
                          </Typography>
                        </Stack>
                      </MessageBubble>
                    </Stack>
                  </motion.div>
                )}
              </ChatBody>

              <ChatFooter>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Mesajınızı yazın..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  multiline
                  maxRows={3}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '20px'
                    }
                  }}
                />
                <IconButton 
                  color="primary" 
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim()}
                  sx={{
                    ml: 1,
                    background: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      background: 'primary.dark'
                    },
                    '&:disabled': {
                      background: 'grey.300'
                    }
                  }}
                >
                  <SendIcon />
                </IconButton>
              </ChatFooter>
            </ChatWindow>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <FloatingButton onClick={toggleChat}>
          <SmartToyIcon sx={{ fontSize: 28 }} />
        </FloatingButton>
      </motion.div>
    </AssistantContainer>
  );
};

export default AIAssistant;